import { buildDecimalUrl, callDecimal } from "../utils.ts";
import { VstLocale } from "../../../utils/locale.ts";
import { getAlohaParams } from "routes/root/helpers/loginHandler.ts";

interface AuthenticatedRedirect {
  redirectUri: string;
}

/**
 * Follows supernova structure conventions. This is not currently enforced by the backend.
 */
export interface UserPreferenceRecord<T = unknown> {
  // NonNullable assists with making sure clears/resets are done by resetting the entire object, not just the value
  // Also plays nicer with form serializations
  value: NonNullable<T>;
  version: number;
  updatedAt: string;
}

export type ActualUserPreference<T> =
  | UserPreferenceRecord<T>
  | EmptyObject // EmptyObject occurs with deletion. Backend limitation from supernova era.
  | undefined;

/**
 * The backend will actually accept any key, but the enum helps with auditing
 * usage, typing the response, and keeping keys used in multiple places synced up
 * correctly
 *
 * This namespace is shared with supernova.
 * https://github.com/vitalsource/supernova/blob/10980d52483a1a47fdef45c3ea35ce369c3b1ff6/src/apps/reader/framework/api/features/preferences/utils.ts#L3
 */
export enum UserPreferenceKey {
  DashboardOnboardingBannerDismissed = "home-dashboard-onboarding-banner-dismissed",
  DashboardRecommendationsBannerDismissed = "home-dashboard-recommendations-banner-dismissed",
  MyLibraryOnboardingBannerDismissed = "home-my-library-onboarding-banner-dismissed",
  ExpiredOnboardingBannerDismissed = "home-expired-onboarding-banner-dismissed",
  HomeSearchHistory = "home-search-history",
  LastSeenExpiredAt = "home-last-seen-expired-at",
  MyCoursesUserSurveyDismissedDate = "home-my-courses-user-survey-dismissed-date",
  MyCoursesVerifyShippingAddressExpiredOn = "home-my-courses-verify-shipping-address-expired-on",
  LastSeenRecommendations = "home-last-seen-recommendations",
  RecommendationsLive = "home-recommendations-live",
  DashboardNewNavBannerDismissed = "home-dashboard-new-nav-banner-dismissed",
}

export interface User {
  email: string;
  firstName: string;
  lastName: string;
  // Include string in case we get something unexpected from the backend
  // Keep VstLocale type for clarity about expected values
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  locale?: VstLocale | string;
  hasAccessCodes: boolean;
  hasAncillaries: boolean;
  hasExpiredLicenses: boolean;
  /**
   * Is the user a reference user
   */
  ssoOnly: boolean;
  reference?: string;
  preferences: Record<UserPreferenceKey, UserPreferenceRecord | EmptyObject>;
  hasVstStoreLicense: boolean;
  instructor: {
    hasRole: boolean;
    verified: boolean;
    // There might be other regions
    verified_regions: ("us" | "au" | "uk")[];
  };
}

export interface DynamicLink {
  previewLink: string;
  shortLink: string;
  warnings: { warningCode: string; warningMessage: string }[];
}

export interface FinAid {
  balance: number;
  currency: string;
}

export function getLogoutUrl() {
  const alohaParams = getAlohaParams();
  const url = buildDecimalUrl("/logout");
  url.searchParams.set("aloha_params", alohaParams.toString());
  return url.toString();
}

export async function userInfo(accessToken?: string | null) {
  let path = "/api/v1/users";
  if (accessToken) {
    path += `?access_token=${accessToken}`;
  }
  return callDecimal<User>(path, {}, { throw401s: false });
}

export async function authenticatedRedirect(url: string) {
  return callDecimal<AuthenticatedRedirect>(
    "/redirects/",
    {
      method: "POST",
      body: JSON.stringify({
        data: {
          redirect_uri: url,
        },
      }),
    },
    {
      throw401s: false,
    }
  );
}

export async function userUpdate({ locale }: { locale: string }) {
  return callDecimal<User>("/api/v1/users", {
    method: "PUT",
    body: JSON.stringify({
      user: {
        locale,
      },
    }),
  });
}

export async function userPreferenceUpdate(
  key: UserPreferenceKey,
  value: NonNullable<unknown>, // Don't reset by setting null/undefined, use the delete method
  version = 0
) {
  return callDecimal("/api/v1/users/preferences", {
    method: "PUT",
    body: JSON.stringify({
      preferences: {
        [key]: {
          version,
          value,
          updatedAt: new Date().toUTCString(),
        },
      },
    }),
  });
}

export async function userPreferenceDelete(key: UserPreferenceKey) {
  return callDecimal("/api/v1/users/preferences", {
    method: "PUT",
    body: JSON.stringify({
      preferences: {
        [key]: {},
      },
    }),
  });
}

export async function generateDynamicLink(destination: string) {
  return callDecimal<DynamicLink>("/api/v1/users/dynamic_link", {
    method: "POST",
    body: JSON.stringify({
      data: {
        destination,
      },
    }),
  });
}

export function getFinAid() {
  return callDecimal<FinAid>("/api/v1/users/finaid");
}
